import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "./components/ui/button";
import { Input } from "./components/ui/input";
import { Badge } from "./components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "./components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "./components/ui/table";
import { Ticket, Plus, Pencil, Archive, Trash2, Users, RotateCcw, X } from "lucide-react";

/**
 * SeasonPassManagement — admin-only management UI for season passes.
 *
 * A season pass groups any set of events (any type: concerts, theatre, dance…)
 * under an admin-picked name and offers up to two priced tiers (e.g. "Standard
 * seat" / "Premium seat"). Members inside the sales window who hold every
 * required active membership can then buy the tier of their choice; on
 * successful Stripe payment the backend auto-generates one paid booking per
 * included event so financial reports stay accurate per-event.
 */
const emptyForm = {
  season_name: "",
  season_year_start: new Date().getFullYear(),
  event_ids: [],
  price_rows: [{ id: null, label: "", amount: "" }],
  required_membership_years_str: "",
  sales_window_start: "",
  sales_window_end: "",
  status: "draft",
};

const SeasonPassManagement = ({ currentUser, onClose }) => {
  const [passes, setPasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [events, setEvents] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingPass, setEditingPass] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [purchasesDialogPass, setPurchasesDialogPass] = useState(null);
  const [purchases, setPurchases] = useState([]);
  const [cancellingId, setCancellingId] = useState(null);
  const [confirmCancel, setConfirmCancel] = useState(null); // purchase object pending confirmation
  const [discardingId, setDiscardingId] = useState(null);
  const [confirmDiscard, setConfirmDiscard] = useState(null);

  const loadPasses = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get("/season-passes?include_drafts=true");
      setPasses(res.data?.season_passes || []);
    } catch (err) {
      toast.error("Failed to load season passes");
    } finally {
      setLoading(false);
    }
  }, []);

  const loadEvents = useCallback(async () => {
    try {
      const res = await axios.get("/events");
      // Response is either a list or {events:[…]}
      const list = Array.isArray(res.data) ? res.data : res.data?.events || [];
      setEvents(list);
    } catch {
      setEvents([]);
    }
  }, []);

  useEffect(() => {
    loadPasses();
    loadEvents();
  }, [loadPasses, loadEvents]);

  const openCreate = () => {
    setEditingPass(null);
    setForm(emptyForm);
    setDialogOpen(true);
  };

  const openEdit = (p) => {
    setEditingPass(p);
    setForm({
      season_name: p.season_name || "",
      season_year_start: p.season_year_start,
      event_ids: p.event_ids || [],
      price_rows: (p.price_rows || []).length > 0
        ? p.price_rows.map((r) => ({ id: r.id, label: r.label, amount: String(r.amount ?? "") }))
        : [{ id: null, label: "", amount: "" }],
      required_membership_years_str: (p.required_membership_years || []).join(","),
      sales_window_start: p.sales_window_start || "",
      sales_window_end: p.sales_window_end || "",
      status: p.status || "draft",
    });
    setDialogOpen(true);
  };

  const saveForm = async () => {
    try {
      if (!form.season_name.trim()) {
        toast.error("Season name is required");
        return;
      }
      const yearsList = form.required_membership_years_str
        .split(",")
        .map((s) => parseInt(s.trim(), 10))
        .filter((n) => Number.isFinite(n));

      const cleanedRows = (form.price_rows || [])
        .filter((r) => (r.label || "").trim() !== "")
        .map((r) => ({
          id: r.id || undefined,
          label: (r.label || "").trim(),
          amount: parseFloat(r.amount) || 0,
        }));

      if (form.status === "published") {
        if (cleanedRows.length < 1) {
          toast.error("A published season pass must have at least one priced row.");
          return;
        }
        if (form.event_ids.length < 1) {
          toast.error("A published season pass must include at least one event.");
          return;
        }
      }

      const payload = {
        season_name: form.season_name.trim(),
        season_year_start: parseInt(form.season_year_start, 10),
        event_ids: form.event_ids,
        price_rows: cleanedRows,
        required_membership_years: yearsList,
        sales_window_start: form.sales_window_start,
        sales_window_end: form.sales_window_end,
        status: form.status,
      };

      if (editingPass) {
        await axios.put(`/season-passes/${editingPass.id}`, payload);
        toast.success("Season pass updated");
      } else {
        await axios.post("/season-passes", payload);
        toast.success("Season pass created");
      }
      setDialogOpen(false);
      loadPasses();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save season pass");
    }
  };

  const updateRow = (idx, patch) => {
    setForm((f) => {
      const rows = [...(f.price_rows || [])];
      rows[idx] = { ...rows[idx], ...patch };
      return { ...f, price_rows: rows };
    });
  };
  const addRow = () => {
    setForm((f) => {
      const rows = [...(f.price_rows || [])];
      if (rows.length >= 2) return f;
      rows.push({ id: null, label: "", amount: "" });
      return { ...f, price_rows: rows };
    });
  };
  const removeRow = (idx) => {
    setForm((f) => {
      const rows = [...(f.price_rows || [])];
      if (rows.length <= 1) return f;
      rows.splice(idx, 1);
      return { ...f, price_rows: rows };
    });
  };

  const archivePass = async (p) => {
    if (!window.confirm(`Archive "${p.season_name}"? It will no longer appear to members.`)) return;
    try {
      await axios.put(`/season-passes/${p.id}`, { status: "archived" });
      toast.success("Season pass archived");
      loadPasses();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to archive");
    }
  };

  const reactivatePass = async (p) => {
    try {
      await axios.put(`/season-passes/${p.id}`, { status: "published" });
      toast.success("Season pass republished");
      loadPasses();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to publish");
    }
  };

  const deletePass = async (p) => {
    if (!window.confirm(`Permanently delete "${p.season_name}"? Only allowed if no member has paid for it.`)) return;
    try {
      await axios.delete(`/season-passes/${p.id}`);
      toast.success("Season pass deleted");
      loadPasses();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete");
    }
  };

  const viewPurchases = async (p) => {
    try {
      const res = await axios.get(`/season-passes/${p.id}/purchases`);
      setPurchases(res.data?.purchases || []);
      setPurchasesDialogPass(p);
    } catch (err) {
      toast.error("Failed to load purchases");
    }
  };

  const cancelPurchase = async (purchase) => {
    if (!purchase || !purchasesDialogPass) return;
    setCancellingId(purchase.id);
    try {
      const res = await axios.post(
        `/season-passes/${purchasesDialogPass.id}/purchases/${purchase.id}/refund`
      );
      const n = res.data?.cancelled_bookings ?? 0;
      toast.success(`Purchase cancelled — ${n} booking${n === 1 ? "" : "s"} refunded.`);
      // Refresh the in-dialog list so status flips to "refunded" without reload.
      const refreshed = await axios.get(`/season-passes/${purchasesDialogPass.id}/purchases`);
      setPurchases(refreshed.data?.purchases || []);
      setConfirmCancel(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not cancel this pass.");
    } finally {
      setCancellingId(null);
    }
  };

  const discardPurchase = async (purchase) => {
    if (!purchase || !purchasesDialogPass) return;
    setDiscardingId(purchase.id);
    try {
      await axios.delete(
        `/season-passes/${purchasesDialogPass.id}/purchases/${purchase.id}`
      );
      toast.success("Abandoned checkout removed from the list.");
      const refreshed = await axios.get(`/season-passes/${purchasesDialogPass.id}/purchases`);
      setPurchases(refreshed.data?.purchases || []);
      setConfirmDiscard(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not discard this purchase.");
    } finally {
      setDiscardingId(null);
    }
  };

  const toggleEventInList = (eventId, checked) => {
    setForm((f) => {
      const cur = new Set(f.event_ids);
      if (checked) cur.add(eventId);
      else cur.delete(eventId);
      return { ...f, event_ids: Array.from(cur) };
    });
  };

  const statusBadgeClasses = (s) =>
    s === "published"
      ? "bg-emerald-100 text-emerald-800 border-emerald-200"
      : s === "archived"
      ? "bg-slate-200 text-slate-700 border-slate-300"
      : "bg-amber-100 text-amber-800 border-amber-200";

  return (
    <div className="space-y-6" data-testid="season-pass-management">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-slate-900 flex items-center gap-2">
            <Ticket className="h-5 w-5 text-purple-600" />
            Season Passes
          </h2>
          <p className="text-sm text-slate-500">
            Manage season passes for any set of events (concerts, theatre…). Only members with the required
            active memberships and inside the sales window can buy.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {onClose && (
            <Button variant="outline" size="sm" onClick={onClose} data-testid="close-season-pass-management">
              Close
            </Button>
          )}
          <Button size="sm" onClick={openCreate} data-testid="add-season-pass-btn">
            <Plus className="h-4 w-4 mr-1" />
            New season pass
          </Button>
        </div>
      </div>

      <div className="rounded-lg border">
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50">
              <TableHead>Season</TableHead>
              <TableHead>Year</TableHead>
              <TableHead>Sales window</TableHead>
              <TableHead>Price rows (€)</TableHead>
              <TableHead>Events</TableHead>
              <TableHead>Membership req.</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={8} className="py-8 text-center text-slate-500">
                  Loading…
                </TableCell>
              </TableRow>
            ) : passes.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} className="py-8 text-center text-slate-500">
                  No season passes yet. Click <strong>New season pass</strong> to create the first one.
                </TableCell>
              </TableRow>
            ) : (
              passes.map((p) => (
                <TableRow key={p.id} data-testid={`season-pass-row-${p.id}`}>
                  <TableCell className="font-medium">{p.season_name}</TableCell>
                  <TableCell className="text-sm">{p.display_year || p.season_year_start}</TableCell>
                  <TableCell className="text-xs text-slate-600 whitespace-nowrap">
                    {p.sales_window_start} → {p.sales_window_end}
                  </TableCell>
                  <TableCell className="text-sm whitespace-nowrap">
                    {(p.price_rows || []).length === 0 ? (
                      <span className="text-xs text-slate-400">no rows</span>
                    ) : (
                      <div className="space-y-0.5">
                        {(p.price_rows || []).map((r) => (
                          <div key={r.id} className="text-xs">
                            <span className="text-slate-500">{r.label}: </span>
                            <strong>€{Number(r.amount || 0).toFixed(2)}</strong>
                          </div>
                        ))}
                      </div>
                    )}
                  </TableCell>
                  <TableCell className="text-sm">{(p.event_ids || []).length}</TableCell>
                  <TableCell className="text-xs">{(p.required_membership_years || []).join(", ") || "—"}</TableCell>
                  <TableCell>
                    <Badge variant="outline" className={`text-xs ${statusBadgeClasses(p.status)}`}>
                      {p.status}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-1 flex-wrap">
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-7 text-xs"
                        onClick={() => viewPurchases(p)}
                        data-testid={`view-purchases-${p.id}`}
                        title="View purchases"
                      >
                        <Users className="h-3 w-3 mr-1" />
                        Purchases
                      </Button>
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-7 w-7"
                        onClick={() => openEdit(p)}
                        data-testid={`edit-season-pass-${p.id}`}
                        title="Edit"
                      >
                        <Pencil className="h-3 w-3" />
                      </Button>
                      {p.status === "archived" ? (
                        <Button
                          size="icon"
                          variant="ghost"
                          className="h-7 w-7 text-emerald-700"
                          onClick={() => reactivatePass(p)}
                          data-testid={`reactivate-${p.id}`}
                          title="Republish"
                        >
                          <RotateCcw className="h-3 w-3" />
                        </Button>
                      ) : (
                        <Button
                          size="icon"
                          variant="ghost"
                          className="h-7 w-7 text-slate-600"
                          onClick={() => archivePass(p)}
                          data-testid={`archive-${p.id}`}
                          title="Archive"
                        >
                          <Archive className="h-3 w-3" />
                        </Button>
                      )}
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-7 w-7 text-red-600 hover:text-red-800"
                        onClick={() => deletePass(p)}
                        data-testid={`delete-${p.id}`}
                        title="Delete (only if no paid purchase)"
                      >
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Create / Edit dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl" data-testid="season-pass-dialog">
          <DialogHeader>
            <DialogTitle>{editingPass ? "Edit season pass" : "New season pass"}</DialogTitle>
            <DialogDescription>
              Add 1 or 2 priced ticket rows (e.g. "Standard seat" / "Premium seat") and pick any events to
              include (any type, any count). A single-row pass is fully supported — members inside the sales
              window who hold the required active memberships can then buy the row of their choice.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium">Season name *</label>
                <Input
                  data-testid="sp-name"
                  value={form.season_name}
                  onChange={(e) => setForm((f) => ({ ...f, season_name: e.target.value }))}
                  placeholder="Symphonic 2026/27"
                />
              </div>
              <div>
                <label className="text-sm font-medium">Season year (start)</label>
                <Input
                  type="number"
                  data-testid="sp-year"
                  value={form.season_year_start}
                  onChange={(e) => setForm((f) => ({ ...f, season_year_start: e.target.value }))}
                />
                <p className="text-xs text-slate-500 mt-1">
                  Displayed as {form.season_year_start}/{String((parseInt(form.season_year_start, 10) || 0) + 1).slice(-2)}
                </p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium">Sales window start *</label>
                <Input
                  type="date"
                  data-testid="sp-window-start"
                  value={form.sales_window_start}
                  onChange={(e) => setForm((f) => ({ ...f, sales_window_start: e.target.value }))}
                />
              </div>
              <div>
                <label className="text-sm font-medium">Sales window end *</label>
                <Input
                  type="date"
                  data-testid="sp-window-end"
                  value={form.sales_window_end}
                  onChange={(e) => setForm((f) => ({ ...f, sales_window_end: e.target.value }))}
                />
              </div>
            </div>

            <div className="rounded-md border border-slate-200 p-3" data-testid="sp-price-rows">
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm font-semibold text-slate-800">Price rows (1 or 2)</p>
                <span className="text-xs text-slate-500">Members pick one at checkout</span>
              </div>
              <p className="text-xs text-slate-500 mb-3">
                Add a label and a final price for each row. Any discount for pass holders is baked in.
              </p>
              <div className="space-y-2">
                {(form.price_rows || []).map((row, idx) => (
                  <div key={idx} className="flex items-center gap-2" data-testid={`sp-price-row-${idx}`}>
                    <span className="text-xs text-slate-500 w-6">#{idx + 1}</span>
                    <Input
                      placeholder="Label (e.g. Standard seat, Premium seat)"
                      value={row.label || ""}
                      onChange={(e) => updateRow(idx, { label: e.target.value })}
                      className="flex-1"
                      data-testid={`sp-price-row-label-${idx}`}
                    />
                    <div className="flex items-center gap-1">
                      <span className="text-sm text-slate-600">€</span>
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        placeholder="0.00"
                        value={row.amount ?? ""}
                        onChange={(e) => updateRow(idx, { amount: e.target.value })}
                        className="w-28"
                        data-testid={`sp-price-row-amount-${idx}`}
                      />
                    </div>
                    <Button
                      type="button"
                      size="icon"
                      variant="ghost"
                      className="h-8 w-8 text-red-600 hover:bg-red-50"
                      disabled={(form.price_rows || []).length <= 1}
                      onClick={() => removeRow(idx)}
                      data-testid={`sp-price-row-remove-${idx}`}
                      title="Remove row"
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>
              {(form.price_rows || []).length < 2 && (
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={addRow}
                  className="mt-2 border-purple-300 text-purple-700 hover:bg-purple-50"
                  data-testid="sp-price-row-add"
                >
                  <Plus className="h-4 w-4 mr-1" /> Add row
                </Button>
              )}
            </div>

            <div>
              <label className="text-sm font-medium">Required active memberships (comma-separated years)</label>
              <Input
                data-testid="sp-req-years"
                placeholder="2026, 2027"
                value={form.required_membership_years_str}
                onChange={(e) => setForm((f) => ({ ...f, required_membership_years_str: e.target.value }))}
              />
              <p className="text-xs text-slate-500 mt-1">
                Members must hold an active membership for every year listed to buy this pass.
              </p>
            </div>

            <div>
              <label className="text-sm font-medium">
                Events included ({form.event_ids.length})
              </label>
              <div className="mt-1 max-h-48 overflow-y-auto rounded-md border p-2 space-y-1">
                {events.length === 0 && <p className="text-xs text-slate-500 italic">No events available.</p>}
                {events.map((ev) => (
                  <label
                    key={ev.id}
                    className="flex items-center gap-2 text-sm px-2 py-1 rounded hover:bg-slate-50 cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={form.event_ids.includes(ev.id)}
                      onChange={(e) => toggleEventInList(ev.id, e.target.checked)}
                      data-testid={`sp-event-${ev.id}`}
                    />
                    <span className="flex-1 truncate">{ev.title}</span>
                    <span className="text-xs text-slate-500">{(ev.event_date || "").slice(0, 10)}</span>
                  </label>
                ))}
              </div>
              <p className="text-xs text-slate-500 mt-1">
                Include any events you like — concerts, theatre, dance, etc. The paid amount is split evenly across every included event on the financial reports.
              </p>
            </div>

            <div>
              <label className="text-sm font-medium">Status</label>
              <Select value={form.status} onValueChange={(v) => setForm((f) => ({ ...f, status: v }))}>
                <SelectTrigger data-testid="sp-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="draft">Draft</SelectItem>
                  <SelectItem value="published">Published</SelectItem>
                  <SelectItem value="archived">Archived</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-500 mt-1">
                A pass is only bookable by members when <strong>Published</strong> and inside its sales window.
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={saveForm} data-testid="save-season-pass-btn">
              {editingPass ? "Save changes" : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Purchases dialog */}
      <Dialog open={!!purchasesDialogPass} onOpenChange={(v) => !v && setPurchasesDialogPass(null)}>
        <DialogContent className="max-w-6xl w-[95vw]">
          <DialogHeader>
            <DialogTitle>{purchasesDialogPass?.season_name} — Purchases</DialogTitle>
            <DialogDescription>
              Every member purchase of this season pass with their linked auto-generated bookings.
              Use the red <strong>Cancel</strong> button on any paid row to refund it and void the linked bookings.
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-[60vh] overflow-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead>Member</TableHead>
                  <TableHead>Partner</TableHead>
                  <TableHead>Rows</TableHead>
                  <TableHead>Amount</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Purchased</TableHead>
                  <TableHead>Events</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {purchases.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} className="py-6 text-center text-slate-500">
                      No purchases yet.
                    </TableCell>
                  </TableRow>
                ) : (
                  purchases.map((p) => (
                    <TableRow key={p.id} data-testid={`purchase-row-${p.id}`}>
                      <TableCell>
                        {p.member ? (
                          <>
                            <div className="font-medium">{p.member.last_name} {p.member.first_name}</div>
                            <div className="text-xs text-slate-500">{p.member.email}{p.member.member_number ? ` · #${p.member.member_number}` : ""}</div>
                          </>
                        ) : (
                          <span className="text-slate-400">Unknown member</span>
                        )}
                      </TableCell>
                      <TableCell>
                        {p.partner ? (
                          <div data-testid={`purchase-partner-${p.id}`}>
                            <div className="font-medium text-sm">{p.partner.last_name} {p.partner.first_name}</div>
                            <div className="text-xs text-slate-500">{p.partner.email}</div>
                          </div>
                        ) : (
                          <span className="text-slate-300 text-xs">—</span>
                        )}
                      </TableCell>
                      <TableCell className="text-xs">
                        {p.price_row_label ? (
                          <Badge variant="outline" className="text-[10px] bg-purple-50 text-purple-800 border-purple-200">
                            {p.price_row_label}
                          </Badge>
                        ) : (
                          <span className="text-slate-400">—</span>
                        )}
                        {p.partner_price_row_label && (
                          <Badge variant="outline" className="ml-1 text-[10px] bg-fuchsia-50 text-fuchsia-800 border-fuchsia-200" data-testid={`purchase-partner-row-${p.id}`}>
                            +{p.partner_price_row_label}
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-sm font-semibold">€{Number(p.amount_paid || 0).toFixed(2)}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className={`text-xs ${p.payment_status === "paid" ? "bg-emerald-50 text-emerald-800 border-emerald-200" : p.payment_status === "refunded" ? "bg-rose-50 text-rose-800 border-rose-200" : "bg-amber-50 text-amber-800 border-amber-200"}`} data-testid={`purchase-status-${p.id}`}>
                          {p.payment_status}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-xs text-slate-600 whitespace-nowrap">
                        {p.purchased_at ? new Date(p.purchased_at).toLocaleDateString() : "—"}
                      </TableCell>
                      <TableCell className="text-xs">{(p.generated_booking_ids || []).length}</TableCell>
                      <TableCell className="text-right whitespace-nowrap">
                        {(String(p.payment_status || "").toLowerCase() === "paid") ? (
                          <Button
                            size="sm"
                            variant="destructive"
                            className="h-7 px-3 text-xs"
                            onClick={() => setConfirmCancel(p)}
                            disabled={cancellingId === p.id}
                            data-testid={`cancel-purchase-${p.id}`}
                          >
                            <X className="h-3 w-3 mr-1" />
                            {cancellingId === p.id ? "Cancelling…" : "Cancel"}
                          </Button>
                        ) : (String(p.payment_status || "").toLowerCase() === "pending") ? (
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-7 px-2 text-xs text-slate-600 hover:bg-rose-50 hover:text-rose-700 hover:border-rose-300"
                            onClick={() => setConfirmDiscard(p)}
                            disabled={discardingId === p.id}
                            data-testid={`discard-purchase-${p.id}`}
                            title="Delete this abandoned Stripe checkout"
                          >
                            <Trash2 className="h-3 w-3 mr-1" />
                            {discardingId === p.id ? "Discarding…" : "Discard"}
                          </Button>
                        ) : (
                          <span className="text-[11px] italic text-slate-400">—</span>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </DialogContent>
      </Dialog>

      {/* Cancel confirmation — destructive action, so a small nested dialog
          keeps the flow explicit. Cancelling refunds the purchase status and
          voids every auto-generated booking (member + partner). */}
      <Dialog open={!!confirmCancel} onOpenChange={(v) => !v && setConfirmCancel(null)}>
        <DialogContent className="max-w-md" data-testid="cancel-purchase-dialog">
          <DialogHeader>
            <DialogTitle>Cancel this season pass?</DialogTitle>
            <DialogDescription>
              This will mark the purchase as refunded and cancel every booking that was auto-generated for the included events
              {confirmCancel?.partner ? " — for both the member and their partner." : "."}
              <br />
              <span className="text-rose-700 font-medium">This action cannot be undone from the app.</span>
            </DialogDescription>
          </DialogHeader>
          {confirmCancel && (
            <div className="rounded-md bg-slate-50 p-3 text-sm space-y-1">
              <div>
                <span className="text-slate-500">Member:</span>{" "}
                <strong>{confirmCancel.member?.last_name} {confirmCancel.member?.first_name}</strong>
              </div>
              {confirmCancel.partner && (
                <div>
                  <span className="text-slate-500">Partner:</span>{" "}
                  <strong>{confirmCancel.partner.last_name} {confirmCancel.partner.first_name}</strong>
                </div>
              )}
              <div>
                <span className="text-slate-500">Amount:</span>{" "}
                <strong>€{Number(confirmCancel.amount_paid || 0).toFixed(2)}</strong>
              </div>
              <div>
                <span className="text-slate-500">Bookings to cancel:</span>{" "}
                <strong>{(confirmCancel.generated_booking_ids || []).length}</strong>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setConfirmCancel(null)}
              disabled={cancellingId === confirmCancel?.id}
              data-testid="cancel-purchase-dismiss"
            >
              Keep it
            </Button>
            <Button
              className="bg-rose-600 hover:bg-rose-700 text-white"
              onClick={() => cancelPurchase(confirmCancel)}
              disabled={cancellingId === confirmCancel?.id}
              data-testid="cancel-purchase-confirm"
            >
              {cancellingId === confirmCancel?.id ? "Cancelling…" : "Yes, cancel pass"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Discard confirmation — for abandoned pending Stripe checkouts. This is
          NOT a refund; it just removes a never-completed row from the list. */}
      <Dialog open={!!confirmDiscard} onOpenChange={(v) => !v && setConfirmDiscard(null)}>
        <DialogContent className="max-w-md" data-testid="discard-purchase-dialog">
          <DialogHeader>
            <DialogTitle>Discard this abandoned checkout?</DialogTitle>
            <DialogDescription>
              This row was created when the member opened the Stripe payment page but never completed the payment.
              No money was charged. Discarding just removes the ghost row from your view.
            </DialogDescription>
          </DialogHeader>
          {confirmDiscard && (
            <div className="rounded-md bg-slate-50 p-3 text-sm space-y-1">
              <div>
                <span className="text-slate-500">Member:</span>{" "}
                <strong>{confirmDiscard.member?.last_name} {confirmDiscard.member?.first_name}</strong>
              </div>
              <div>
                <span className="text-slate-500">Would-have-paid:</span>{" "}
                <strong>€{Number(confirmDiscard.amount_paid || 0).toFixed(2)}</strong>
              </div>
              <div>
                <span className="text-slate-500">Status:</span>{" "}
                <Badge variant="outline" className="text-[10px] bg-amber-50 text-amber-800 border-amber-200">pending (never paid)</Badge>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setConfirmDiscard(null)}
              disabled={discardingId === confirmDiscard?.id}
              data-testid="discard-purchase-dismiss"
            >
              Keep it
            </Button>
            <Button
              className="bg-rose-600 hover:bg-rose-700 text-white"
              onClick={() => discardPurchase(confirmDiscard)}
              disabled={discardingId === confirmDiscard?.id}
              data-testid="discard-purchase-confirm"
            >
              {discardingId === confirmDiscard?.id ? "Discarding…" : "Yes, discard"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SeasonPassManagement;
